import { supabase } from '../lib/supabase';

export const fileUploadService = {
  // Upload file to order attachments bucket
  async uploadOrderAttachment(orderIds, file, metadata = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) {
        throw new Error('No authenticated user');
      }

      // Generate unique file path
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${Date?.now()}-${Math?.random()?.toString(36)?.substring(2)}.${fileExt}`;
      const filePath = `${orderIds}/${fileName}`;

      // Upload to storage
      const { data: uploadData, error: uploadError } = await supabase?.storage
        ?.from('order-attachments')
        ?.upload(filePath, file, {
          upsert: false,
          contentType: file?.type
        });

      if (uploadError) throw uploadError;

      // Create attachment record
      const attachmentData = {
        order_id: orderIds,
        file_name: file?.name,
        storage_path: uploadData?.path,
        storage_bucket: 'order-attachments',
        file_size: file?.size,
        mime_type: file?.type,
        file_type: this.getFileType(file?.type),
        uploaded_by_id: user?.id,
        description: metadata?.description || null,
        tags: metadata?.tags || null,
        is_public: false
      };

      const { data: attachmentRecord, error: recordError } = await supabase
        ?.from('attachments')
        ?.insert([attachmentData])
        ?.select()
        ?.single();

      if (recordError) {
        // Clean up uploaded file if database insert fails
        await supabase?.storage?.from('order-attachments')?.remove([filePath]);
        throw recordError;
      }

      return { data: attachmentRecord, error: null };
    } catch (error) {
      console.error('Error uploading file:', error);
      return { data: null, error };
    }
  },

  // Upload user avatar
  async uploadUserAvatar(file) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) {
        throw new Error('No authenticated user');
      }

      // Generate file path for user avatar
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `avatar.${fileExt}`;
      const filePath = `${user?.id}/${fileName}`;

      // Upload to public storage
      const { data: uploadData, error: uploadError } = await supabase?.storage
        ?.from('user-avatars')
        ?.upload(filePath, file, {
          upsert: true, // Replace existing avatar
          contentType: file?.type
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase?.storage
        ?.from('user-avatars')
        ?.getPublicUrl(uploadData?.path);

      // Update user profile with avatar URL
      const { data: profileData, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.update({
          avatar_url: publicUrl,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', user?.id)
        ?.select()
        ?.single();

      if (profileError) throw profileError;

      return { data: { avatar_url: publicUrl, profile: profileData }, error: null };
    } catch (error) {
      console.error('Error uploading avatar:', error);
      return { data: null, error };
    }
  },

  // Generate export report and store in bucket
  async generateExportReport(reportData, fileName) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) {
        throw new Error('No authenticated user');
      }

      // Convert data to CSV or JSON blob
      const blob = new Blob([JSON?.stringify(reportData, null, 2)], {
        type: 'application/json'
      });

      // Generate file path
      const timestamp = new Date()?.toISOString()?.split('T')?.[0];
      const filePath = `${user?.id}/${timestamp}/${fileName}`;

      // Upload to export reports bucket
      const { data: uploadData, error: uploadError } = await supabase?.storage
        ?.from('export-reports')
        ?.upload(filePath, blob, {
          upsert: false,
          contentType: 'application/json'
        });

      if (uploadError) throw uploadError;

      // Generate signed URL for download (expires in 1 hour)
      const { data: signedUrl, error: urlError } = await supabase?.storage
        ?.from('export-reports')
        ?.createSignedUrl(uploadData?.path, 3600);

      if (urlError) throw urlError;

      return { 
        data: { 
          filePath: uploadData?.path,
          downloadUrl: signedUrl?.signedUrl,
          expiresAt: new Date(Date?.now() + 3600000)?.toISOString()
        }, 
        error: null 
      };
    } catch (error) {
      console.error('Error generating export report:', error);
      return { data: null, error };
    }
  },

  // Download attachment
  async downloadAttachment(attachmentId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) {
        throw new Error('No authenticated user');
      }

      // Get attachment record
      const { data: attachment, error: attachmentError } = await supabase
        ?.from('attachments')
        ?.select('*, order:orders(id)')
        ?.eq('id', attachmentId)
        ?.single();

      if (attachmentError) throw attachmentError;

      // Check if user can access this attachment (via order access)
      const { data: canAccess } = await supabase
        ?.rpc('can_access_order', { order_uuid: attachment?.order?.id });

      if (!canAccess) {
        throw new Error('Access denied to this attachment');
      }

      // Download file from storage
      const { data: fileData, error: downloadError } = await supabase?.storage
        ?.from(attachment?.storage_bucket)
        ?.download(attachment?.storage_path);

      if (downloadError) throw downloadError;

      return { 
        data: {
          file: fileData,
          filename: attachment?.file_name,
          mimeType: attachment?.mime_type
        }, 
        error: null 
      };
    } catch (error) {
      console.error('Error downloading attachment:', error);
      return { data: null, error };
    }
  },

  // Delete attachment
  async deleteAttachment(attachmentId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) {
        throw new Error('No authenticated user');
      }

      // Get attachment record to get storage path
      const { data: attachment, error: attachmentError } = await supabase
        ?.from('attachments')
        ?.select('*')
        ?.eq('id', attachmentId)
        ?.single();

      if (attachmentError) throw attachmentError;

      // Delete from storage
      const { error: storageError } = await supabase?.storage
        ?.from(attachment?.storage_bucket)
        ?.remove([attachment?.storage_path]);

      if (storageError) {
        console.warn('Could not delete file from storage:', storageError);
      }

      // Delete attachment record
      const { error: recordError } = await supabase
        ?.from('attachments')
        ?.delete()
        ?.eq('id', attachmentId);

      if (recordError) throw recordError;

      return { data: true, error: null };
    } catch (error) {
      console.error('Error deleting attachment:', error);
      return { data: null, error };
    }
  },

  // Get attachments for an order
  async getOrderAttachments(orderId) {
    try {
      const { data, error } = await supabase
        ?.from('attachments')
        ?.select(`
          *,
          uploaded_by:user_profiles!uploaded_by_id(full_name, email)
        `)
        ?.eq('order_id', orderId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      // Add download URLs for each attachment
      const attachmentsWithUrls = await Promise?.all(
        data?.map(async (attachment) => {
          if (attachment?.is_public) {
            // Get public URL
            const { data: { publicUrl } } = supabase?.storage
              ?.from(attachment?.storage_bucket)
              ?.getPublicUrl(attachment?.storage_path);
            
            return { ...attachment, download_url: publicUrl };
          } else {
            // Generate signed URL (expires in 1 hour)
            const { data: signedUrl } = await supabase?.storage
              ?.from(attachment?.storage_bucket)
              ?.createSignedUrl(attachment?.storage_path, 3600);
            
            return { ...attachment, download_url: signedUrl?.signedUrl };
          }
        })
      );

      return { data: attachmentsWithUrls, error: null };
    } catch (error) {
      console.error('Error fetching order attachments:', error);
      return { data: null, error };
    }
  },

  // Helper function to determine file type from MIME type
  getFileType(mimeType) {
    if (!mimeType) return 'other';
    
    if (mimeType?.startsWith('image/')) return 'image';
    if (mimeType?.startsWith('video/')) return 'video';
    if (mimeType?.startsWith('audio/')) return 'audio';
    if (mimeType === 'application/pdf') return 'pdf';
    if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet')) return 'excel';
    if (mimeType?.includes('document') || mimeType?.includes('word')) return 'document';
    
    return 'other';
  },

  // Bulk upload multiple files
  async bulkUploadOrderAttachments(orderId, files, metadata = {}) {
    try {
      const results = [];
      const errors = [];

      for (const file of files) {
        const result = await this.uploadOrderAttachment(orderId, file, metadata);
        
        if (result?.error) {
          errors?.push({ file: file?.name, error: result?.error });
        } else {
          results?.push(result?.data);
        }
      }

      return { 
        data: results, 
        errors: errors?.length > 0 ? errors : null,
        error: null 
      };
    } catch (error) {
      console.error('Error in bulk upload:', error);
      return { data: null, errors: null, error };
    }
  },

  // Clean up old export reports (utility function)
  async cleanupOldExports(daysToKeep = 7) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) {
        throw new Error('No authenticated user');
      }

      const cutoffDate = new Date();
      cutoffDate?.setDate(cutoffDate?.getDate() - daysToKeep);

      // List files in user's export folder
      const { data: files, error: listError } = await supabase?.storage
        ?.from('export-reports')
        ?.list(user?.id);

      if (listError) throw listError;

      // Filter old files
      const oldFiles = files?.filter(file => {
        const fileDate = new Date(file?.created_at);
        return fileDate < cutoffDate;
      });

      // Delete old files
      if (oldFiles?.length > 0) {
        const filePaths = oldFiles?.map(file => `${user?.id}/${file?.name}`);
        const { error: deleteError } = await supabase?.storage
          ?.from('export-reports')
          ?.remove(filePaths);

        if (deleteError) throw deleteError;
      }

      return { data: `Cleaned up ${oldFiles?.length} old export files`, error: null };
    } catch (error) {
      console.error('Error cleaning up exports:', error);
      return { data: null, error };
    }
  }
};

export default fileUploadService;